import React, {Component} from "react";
import {isEqual} from "lodash";
import {connect} from "react-redux";

import MultiSelectList from "./MultiSelectList";
import {getColumns} from "../../helper/multiselectColumns";
import {showNotification} from "../../../../store/slices/admin/notification";
import {angleDown, angleRight} from "../../icons";
import { IBigscreenSettings, ICustomMetrics, IEventField } from "../../../../constants/interfaces";
import computeEventFields from "../../../../hooks/computeEventFields";
import computeCustomFields from "../../../../hooks/computeCustomFields";

const EVENT_FIELD_FLAG = 'EVNT:';

type Props = {
    updateConfig: Function,
    selectedColumns: Array<string>,
    type: string,
    isEdit: boolean,
    brands: Array<string>,
    initialEventFields: IEventField[],
    isLoadingBigscreenSettings: boolean,
    initialCustomFields?: ICustomMetrics[],
    bigscreenSettings?: IBigscreenSettings[],
    // injected
    dispatch: Function
}

type State = {
    availableColumns: { name: string; title: string; }[],
    selectedColumns: { name: string; title: string; }[],
    selectedLeft: { name: string; title: string; }[],
    selectedRight: { name: string; title: string; }[],
    lastSelectedLeft: { name: string; title: string; } | undefined,
    lastSelectedRight: { name: string; title: string; } | undefined,
    initIndexLeft: number | undefined,
    initIndexRight: number | undefined,
    mouseDownLeft: boolean,
    mouseDownRight: boolean,
}

class MultiSelect extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        
        this.state = {
            availableColumns: [],
            selectedColumns: [],
            selectedLeft: [],
            selectedRight: [],
            lastSelectedLeft: undefined,
            lastSelectedRight: undefined,
            initIndexLeft: undefined,
            initIndexRight: undefined,
            mouseDownLeft: false,
            mouseDownRight: false,
        }
        
        this.onDoubleClickLeft = this.onDoubleClickLeft.bind(this);
        this.onDoubleClickRight = this.onDoubleClickRight.bind(this);
        this.onMoveItems = this.onMoveItems.bind(this);
        this.onSelectLeft = this.onSelectLeft.bind(this);
        this.onSelectRight = this.onSelectRight.bind(this);
        this.onMouseUpLeft = this.onMouseUpLeft.bind(this);
        this.onMouseUpRight = this.onMouseUpRight.bind(this);
        this.onMouseDownLeft = this.onMouseDownLeft.bind(this);
        this.onMouseDownRight = this.onMouseDownRight.bind(this);
        this.onMouseOverLeft = this.onMouseOverLeft.bind(this);
        this.onMouseOverRight = this.onMouseOverRight.bind(this);
        this.onMouseEnterLeft = this.onMouseEnterLeft.bind(this);
        this.onMouseEnterRight = this.onMouseEnterRight.bind(this);
        this.onOrderChange = this.onOrderChange.bind(this);
    }

    static defaultProps = {
        initialEventFields: [],
        isLoadingBigscreenSettings: false
    }
    
    componentDidMount() {
        this.setInitState();
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (!isEqual(prevProps.bigscreenSettings, this.props.bigscreenSettings)) {
            const availableColumns = getColumns(this.props.type).filter((item: { name: string, title: string }) => !this.props.selectedColumns.find(defaultItem => item.name === defaultItem));
            let selectedColumns = this.state.selectedColumns;
         
            if (this.props.bigscreenSettings) {
                const eventFields = computeEventFields(this.props.bigscreenSettings);
                const customFields = computeCustomFields(this.props.bigscreenSettings);

                selectedColumns = selectedColumns.filter(column => {
                    return (
                        getColumns(this.props.type).some(c => c.name === column.name)
                        || (eventFields.some(e => `${EVENT_FIELD_FLAG}${e.name}` === column.name))
                        || (customFields.some(cm => cm.id === column.name))
                    );
                });
                const selectedColumnTitles = this.state.selectedColumns.map(column => column.title);

                if (eventFields) {
                    eventFields.forEach(eventField => {
                        if (selectedColumnTitles.indexOf(eventField.label) === -1) {
                            availableColumns.push({
                                name: `${EVENT_FIELD_FLAG}${eventField.name}`,
                                title: eventField.label
                            });
                        }
                    });
                }

                if (customFields) {
                    customFields.forEach(metric => {
                        if (selectedColumnTitles.indexOf(metric.label) === -1) {
                            availableColumns.push({
                                name: metric.id,
                                title: metric.label
                            });
                        }
                    });
                }
            }
            
            this.setState({ availableColumns, selectedColumns });
        }

        if (!isEqual(prevState.selectedColumns, this.state.selectedColumns)) {
            this.props.updateConfig(['fields', "custom_fields", 'event_fields'], this.formatFields(this.state.selectedColumns));
        }
    }

	formatFields(columns:{ name: string; title: string; }[]) {
        const fields: Array<string> = [];
        const event_Fields: IEventField[] = [];
        const custom_fields: ICustomMetrics[] = [];

        let customFields: ICustomMetrics[] = [];
        let customFieldsList: Array<string> = [];

        if (this.props.bigscreenSettings) {
            customFields = computeCustomFields(this.props.bigscreenSettings);
            customFieldsList = customFields.map(m => m.id);
        }

        columns.forEach(column => {
            if (column.name.indexOf(EVENT_FIELD_FLAG) !== -1) {
                const eventName = column.name.slice(EVENT_FIELD_FLAG.length);
                event_Fields.push({
                    brand_id: eventName.split('--')[0],
                    name: eventName,
                    label: column.title,
                    short_label: column.title.split(": ")[1]
                });
            } else if (customFieldsList.includes(column.name)) {
                const customMetric = customFields.find(metric => metric.id === column.name);
                if (customMetric) {
                    custom_fields.push({
                        id: customMetric.id,
                        label: customMetric.label,
                        format: customMetric.format
                    });
                }
            } else {
                fields.push(column.name);
            }
        });

        return [fields, custom_fields, event_Fields];
	}

    setInitState() {
        const availableColumns = getColumns(this.props.type).filter((item: {name: string, title: string}) => !this.props.selectedColumns.find(defaultItem => item.name === defaultItem));
        let selectedColumns = getColumns(this.props.type).filter((item: {name: string, title: string}) => !!this.props.selectedColumns.find(defaultItem => item.name === defaultItem));
        
        const sorted: any[] = [];
        /* eslint-disable */
        this.props.selectedColumns.map((item) => {
            const column = selectedColumns.find((column: { name: string, title: string }) => column.name === item);
            if (column) {
                sorted.push(column);
            }
        });
        /* eslint-disable */
        selectedColumns = sorted;

        if (this.props.initialEventFields) {
            this.props.initialEventFields.forEach(eventField => {
                selectedColumns.push({
                    name: `${EVENT_FIELD_FLAG}${eventField.name}`,
                    title: eventField.label
                });
            });
        }

        if (this.props.initialCustomFields) {
            this.props.initialCustomFields.forEach(customField => {
                selectedColumns.push({
                    name: customField.id,
                    title: customField.label
                });
            });
        }

        const selectedColumnTitles = selectedColumns.map(column => column.title);

        if (this.props.bigscreenSettings) {
            const eventFields = computeEventFields(this.props.bigscreenSettings);
            const customFields = computeCustomFields(this.props.bigscreenSettings);

            eventFields.forEach(eventField => {
                if (selectedColumnTitles.indexOf(eventField.label) === -1) {
                    availableColumns.push({
                        name: `${EVENT_FIELD_FLAG}${eventField.name}`,
                        title: eventField.label
                    });
                }
            });

            customFields.forEach(metric => {
                if (selectedColumnTitles.indexOf(metric.label) === -1) {
                    availableColumns.push({
                        name: metric.id,
                        title: metric.label
                    });
                }
            });
        }
        
        this.setState({
            availableColumns,
            selectedColumns
        });
    }
    
    onDoubleClickLeft(column: {name: string, title: string}) {
        const availableColumns = this.state.availableColumns.filter(item => item.name !== column.name);
        let selectedColumns = [...this.state.selectedColumns];
        const isLimitExceeded = this.isLimitExceeded();
        if (isLimitExceeded)  {
            this.props.dispatch(showNotification('warning', 'Exceeded limit for selected list columns'));
        } else {
            selectedColumns.push(column);
            let selectedLeft = this.state.selectedLeft;
            selectedLeft.splice(selectedLeft.indexOf(column), 1);

            this.setState({
                availableColumns,
                selectedColumns,
                selectedLeft: selectedLeft,
                lastSelectedLeft: column,
            });
        }
        
    }
    
    onDoubleClickRight(column: {name: string, title: string}) {
        const selectedColumns = this.state.selectedColumns.filter(item => item.name !== column.name);
        let availableColumns = [...this.state.availableColumns];
        availableColumns.push(column);
        let selectedRight = this.state.selectedRight;
        selectedRight.splice(selectedRight.indexOf(column), 1);

        this.setState({
            availableColumns,
            selectedColumns,
            selectedRight: selectedRight,
            lastSelectedRight: column,
        });
    }
    
    onSelectLeft(column: { name: string; title: string; }, e: any) {
        let left;
        let lastSelected = column;
        if (e.ctrlKey) {
            left = this.state.selectedLeft;
            if (left.indexOf(column) === -1) {
                left.push(column);
            } else {
                const index = left.indexOf(column);
                left = left.splice(index, 1);
            }
        } else if (e.shiftKey) {
            left = this.state.selectedLeft;
            const items = this.state.availableColumns;
            if (this.state.lastSelectedLeft) {
                lastSelected = this.state.lastSelectedLeft;
                let position1 = items.indexOf(lastSelected);
                let position2 = items.indexOf(column);
                let start = position1 > position2 ? position2 : position1;
                let end = position1 > position2 ? position1 : position2;

                left = items.slice(start, end + 1);
            } else {
                left.push(column);
            }
        } else {
            left = [column];
        }

        this.setState({
            selectedLeft: left,
            lastSelectedLeft: lastSelected,
            selectedRight: [],
            lastSelectedRight: undefined,
        });
    }

    onSelectRight(column: { name: string; title: string; }, e: any) {
        let right;
        let lastSelected = column;
        if (e.ctrlKey) {
            right = this.state.selectedRight;
            if (right.indexOf(column) === -1) {
                right.push(column);
            } else {
                const index = right.indexOf(column);
                right.splice(index, 1);
            }
        } else if (e.shiftKey) {
            right = this.state.selectedRight;
            const items = [...this.state.selectedColumns];
            if (this.state.lastSelectedRight) {
                lastSelected = this.state.lastSelectedRight;
                let position1 = items.indexOf(lastSelected);
                let position2 = items.indexOf(column);
                let start = position1 > position2 ? position2 : position1;
                let end = position1 > position2 ? position1 : position2;

                right = items.slice(start, end + 1);
            } else {
                right.push(column);
            }
        } else  {
            right = [column];
        }

        this.setState({
            selectedRight: right,
            selectedLeft: [],
            lastSelectedRight: lastSelected,
            lastSelectedLeft: undefined,
        });
    }

    onMoveItems(direction: number) {
        if (direction && this.state.selectedRight.length) {
            let availableColumns = [...this.state.availableColumns];
            this.state.selectedRight.map(item => availableColumns.push(item));
            const selectedColumns = this.state.selectedColumns.filter(item => this.state.selectedRight.indexOf(item) === -1);
            this.setState({
                selectedRight: [],
                availableColumns,
                selectedColumns,
                lastSelectedRight: undefined,
            });
        } else if (!direction && this.state.selectedLeft.length) {
            let selectedColumns = [...this.state.selectedColumns];
            const isLimitExceeded = this.isLimitExceeded();
            if (isLimitExceeded) {
                this.props.dispatch(showNotification('warning', 'Exceeded limit for selected list columns'));
            } else {
                this.state.selectedLeft.map(item => selectedColumns.push(item));
                const availableColumns = this.state.availableColumns.filter(item => this.state.selectedLeft.indexOf(item) === -1);
                this.setState({
                    selectedLeft: [],
                    selectedColumns,
                    availableColumns,
                    lastSelectedLeft: undefined,
                });
            }
         
        }
    }

    onMouseUpLeft() {
        this.setState({mouseDownLeft: false});
    }

    onMouseUpRight() {
        this.setState({mouseDownRight: false});
    }

    onMouseDownLeft(e: any, item: { name: string; title: string; }, index: number) {
        if (e.ctrlKey || e.shiftKey) {
            return;
        }

        const selected = [item];
        this.setState({
            mouseDownLeft: true,
            mouseDownRight: false,
            selectedLeft: selected,
            selectedRight: [],
            initIndexLeft: index,
        });
    }

    onMouseDownRight(e: any, item: { name: string; title: string; }, index: number) {
        if (e.ctrlKey || e.shiftKey) {
            return;
        }

        const selected = [item];
        this.setState({
            mouseDownRight: true,
            mouseDownLeft: false,
            selectedRight: selected,
            selectedLeft: [],
            initIndexRight: index,
        });
    }

    onMouseOverLeft(item: { name: string; title: string; }, index: number) {
        let selectedLeft = [...this.state.availableColumns];
        const [fromIndex, toIndex] =  index >= this.state.initIndexLeft! ? [this.state.initIndexLeft, index] : [index, this.state.initIndexLeft];

        this.setState({
            selectedLeft: selectedLeft.splice(fromIndex!, toIndex! - fromIndex! + 1),
        });
    }

    onMouseOverRight(item: { name: string; title: string; }, index: number) {
        let selectedLeft = [...this.state.selectedColumns];
        const [fromIndex, toIndex] =  index >= this.state.initIndexRight! ? [this.state.initIndexRight, index] : [index, this.state.initIndexRight];

        this.setState({
            selectedRight: selectedLeft.splice(fromIndex!, toIndex! - fromIndex! + 1),
        });
    }

    onMouseEnterLeft(e: any) {
        const primaryClicked = e.buttons; // Value will be 1 if primary button is clicked
        if (primaryClicked !== 1 && this.state.mouseDownLeft) {
            this.setState({mouseDownLeft: false});
        }
    }

    onMouseEnterRight(e: any) {
        const primaryClicked = e.buttons; // Value will be 1 if primary button is clicked
        if (primaryClicked !== 1 && this.state.mouseDownRight) {
            this.setState({mouseDownRight: false});
        }
    }

    onOrderChange(order: number) {
        if (this.state.selectedRight.length > 0 && this.state.selectedRight.length !== this.state.selectedColumns.length) {
            let right = [...this.state.selectedColumns];
            let itemsOldOrder = this.state.selectedRight.map(item => right.indexOf(item));
            itemsOldOrder.sort((a, b) => order ? a - b : b - a);
            let newOrder = [];
            let tempItem;
            for (let i = 0; i < itemsOldOrder.length; i++) {
                if ((order && !itemsOldOrder[i]) || (!order && itemsOldOrder[i] === this.state.selectedColumns.length - 1)) {
                    newOrder.push(itemsOldOrder[i]);
                } else {
                    const newPosition = itemsOldOrder[i] + (order ? -1 : 1);
                    if (newOrder.indexOf(newPosition) === -1) {
                        newOrder.push(newPosition);
                        tempItem = right[newPosition];
                        right[newPosition] = right[itemsOldOrder[i]];
                        right[itemsOldOrder[i]] = tempItem;
                    } else {
                        newOrder.push(itemsOldOrder[i]);
                    }
                }
            }

            this.setState({selectedColumns: right});
        }
    }

    isLimitExceeded() {
        const selectedColumns = this.state.selectedColumns;
        const selectedLeft = this.state.selectedLeft;
        return selectedColumns.length > 5 || (selectedLeft.length + selectedColumns.length) > 6;
    }
    
    render() {
        return (
            <div className="config-item__multiselect">
                <div className="config-item__multiselect-lists">
                    <h4 className={`config-item__multiselect-availble-cols-title ${this.props.isLoadingBigscreenSettings ? 'loading' : ''}`}>
                        <span>Available columns</span>
                        <span className="loading-sign">
                            <span className="loading-sign__spinner">
                            </span>
                        </span>
                    </h4>
                    <h4>Selected columns</h4>
                    
                    <MultiSelectList 
                        columns={this.state.availableColumns}
                        selected={this.state.selectedLeft}
                        onDoubleClick={this.onDoubleClickLeft}
                        onSelect={this.onSelectLeft}
                        isMouseDown={this.state.mouseDownLeft}
                        onMouseUp={this.onMouseUpLeft}
                        onMouseDown={this.onMouseDownLeft}
                        onMouseOver={this.onMouseOverLeft}
                        onMouseEnter={this.onMouseEnterLeft}
                    />
                    
                    <div className="config-item__multiselect-btns">
                        <button className="multiselect-btn right" onClick={() => this.onMoveItems(0)}>
                            <span>{angleRight}</span>
                        </button>
                        <button className="multiselect-btn left" onClick={() => this.onMoveItems(1)}>
                            <span>{angleRight}</span>
                        </button>
                    </div>

                    <MultiSelectList 
                        columns={this.state.selectedColumns}
                        selected={this.state.selectedRight}
                        onDoubleClick={this.onDoubleClickRight}
                        onSelect={this.onSelectRight}
                        isMouseDown={this.state.mouseDownRight}
                        onMouseUp={this.onMouseUpRight}
                        onMouseDown={this.onMouseDownRight}
                        onMouseOver={this.onMouseOverRight}
                        onMouseEnter={this.onMouseEnterRight}
                    />
                   
                    <div className="config-item__multiselect-btns">
                        <button className="multiselect-btn up" onClick={() => this.onOrderChange(1)}>
                            <span>{angleDown}</span>
                        </button>
                        <button className="multiselect-btn down" onClick={() => this.onOrderChange(0)}>
                            <span>{angleDown}</span>
                        </button>
                    </div>
                </div>
                <span className="config-item__multiselect-label">Max. 6 columns</span>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => {
    return {
        isLoadingBigscreenSettings: state.bigscreenSettings ? state.bigscreenSettings.isLoading : {}
    }
};

export default connect(mapStateToProps)(MultiSelect);
